import { filterDateRangesByNights, computeBundleDataForOffer, resolveFlightPriceForFareClass, resolveOriginalFlightPriceForFareClass, useSeatEligiblePassengers } from './utils'
import { useContext, useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { createSelector } from 'reselect'
import CurrencyContext from 'contexts/currencyContext'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { toBaseFareClass } from 'constants/flight'
import type { UseOfferCharterCalendarDataParams, FlightCalendarData, ResolvedFlightInfo } from './types'
import { useBundleFlightData } from './useBundleFlightData'

/**
 * Custom hook to handle charter bundle calendar data for a single offer.
 *
 * Uses the shared allocatePackagesForDate logic (same as search page) to
 * combine hotel calendar data with flight prices into bundle pricing.
 *
 * Calendar data must be fetched by the parent component (e.g., FlashHotelContent)
 * via fetchCalendarsByOccupancy. Flight data is fetched by useBundleFlightData.
 *
 * When packageUniqueKey is provided, only that package is considered (offer page).
 * When omitted, all matching-duration packages are evaluated (like search page).
 */
export function useOfferCharterCalendarData(params: UseOfferCharterCalendarDataParams): FlightCalendarData {
  const {
    offer,
    nights,
    rooms,
    fareClass,
    dateRangesByMonth,
    charterBundleRoute,
    packageUniqueKey,
    feederOriginAirportCode,
  } = params

  const currencyContext = useContext(CurrencyContext)
  const currency: string = currencyContext || 'AUD'

  const totalPassengers = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const seatEligiblePassengers = useSeatEligiblePassengers(rooms)

  const shouldFetchData = !!charterBundleRoute

  // Internal guard: some callers (e.g. useCharterBundleFallbackPricing) pass
  // unfiltered date ranges. Calendar-based callers may pre-filter, making this
  // a harmless no-op in that case.
  const filteredDateRangesByMonth = useMemo(
    () => filterDateRangesByNights(dateRangesByMonth, nights),
    [dateRangesByMonth, nights],
  )

  const durationPackages = useMemo(() => {
    if (packageUniqueKey) {
      return offer.packages.filter(pkg => pkg.uniqueKey === packageUniqueKey)
    }
    return offer.packages.filter(pkg => pkg.duration === nights)
  }, [offer.packages, packageUniqueKey, nights])

  const hasNoPackage = durationPackages.length === 0

  const occupancyKeys = useMemo(
    () => rooms.map(room => generateOccupancyStringByRoom(room)),
    [rooms],
  )

  const selectCalendarData = useMemo(
    () => createSelector(
      (state: App.State) => state.calendar.calendarsByOccupancy,
      (calendarsByOccupancy): Record<string, Record<string, App.Calendar>> => {
        if (!shouldFetchData || hasNoPackage) return {}

        const result: Record<string, Record<string, App.Calendar>> = {}
        for (const key of occupancyKeys) {
          const slice = calendarsByOccupancy[key]
          if (slice) result[key] = slice
        }
        return result
      },
    ),
    [occupancyKeys, shouldFetchData, hasNoPackage],
  )

  const calendarDataByOccupancy = useAppSelector(selectCalendarData)

  const isCalendarLoading = useAppSelector(state => {
    if (!shouldFetchData || hasNoPackage) return false
    return rooms.some(room => {
      const occupancyKey = generateOccupancyStringByRoom(room)
      return durationPackages.some(pkg => {
        const loadingFlag = state.calendar.calendarsByOccupancyLoading[occupancyKey]?.[pkg.uniqueKey]
        const hasData = !!state.calendar.calendarsByOccupancy[occupancyKey]?.[pkg.uniqueKey]
        return loadingFlag || (!hasData && loadingFlag === undefined)
      })
    })
  })

  const { flightDataByDate } = useBundleFlightData({
    rooms,
    dateRangesByMonth: shouldFetchData ? filteredDateRangesByMonth : {},
    fareClass,
    currency,
    charterBundleRoute,
    disabled: !shouldFetchData,
    seatEligiblePassengers,
    feederOriginAirportCode,
  })

  const baseFareClass = toBaseFareClass(fareClass)

  const resolvedFlightByDate = useMemo(() => {
    const resolved: Record<string, ResolvedFlightInfo> = {}
    for (const [checkIn, data] of Object.entries(flightDataByDate)) {
      resolved[checkIn] = {
        price: resolveFlightPriceForFareClass(data, baseFareClass, seatEligiblePassengers),
        originalPrice: resolveOriginalFlightPriceForFareClass(data, baseFareClass),
        fetching: data.fetching,
        seatsLeft: data.seatsLeft,
        promotionSeatsLeft: data.promotionSeatsLeft,
        isPromotionPriceApplied: data.isPromotionPriceApplied,
        fnfSeatsLeft: data.fnfSeatsLeft,
        isFnfPriceApplied: data.isFnfPriceApplied,
      }
    }
    return resolved
  }, [flightDataByDate, baseFareClass, seatEligiblePassengers])

  const itemBundleDataByDate = useMemo(() => {
    const allDates = Object.values(filteredDateRangesByMonth).flat()
    const entries = computeBundleDataForOffer({
      dateRanges: allDates,
      offer,
      rooms,
      durationPackages,
      calendarDataByOccupancy,
      resolvedFlightByDate,
      totalPassengers,
      seatEligiblePassengers,
      isCalendarLoading,
      hasNoPackage,
    })
    const dataMap: Record<string, typeof entries[string]['bundleData']> = {}
    for (const [checkIn, entry] of Object.entries(entries)) {
      dataMap[checkIn] = entry.bundleData
    }
    return dataMap
  }, [
    filteredDateRangesByMonth,
    resolvedFlightByDate,
    calendarDataByOccupancy,
    isCalendarLoading,
    offer,
    rooms,
    durationPackages,
    totalPassengers,
    seatEligiblePassengers,
    hasNoPackage,
  ])

  return {
    itemBundleDataByDate,
  }
}
